import { isEmpty, isNil, forEach } from 'lodash';
import { treeNodeType, treeType } from '@/constants/tree';

/**
 * 관리 대상 트리 이벤트 상태 반영 믹스인
 * @type {Object}
 */
export default {
  props: {
    /**
     * 트리 노드별 이벤트 상태 객체
     * @type {Object}
     */
    eventStatus: {
      type: Object,
    },
  },
  computed: {
    /**
     * 이벤트 등급 정보 객체
     * @author Ju
     * @return {Object]
     */
    eventlevelInfo() {
      return this.$store.getters.eventlevelInfo;
    },
  },
  watch: {
    eventStatus: {
      immediate: true,
      handler() {
        if (this.dynamicFlag || !isEmpty(this.treeData)) {
          this.$nextTick(() => {
            const root = this.$refs[this.treeRef].root;
            if (!isNil(root)) {
              this.setEventStatusToNodes(root.childNodes);
            }
          });
        }
      },
    },
  },
  methods: {
    /**
     * 트리 노드 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array}
     */
    setEventStatusToNodes(nodes) {
      if (!isEmpty(nodes)) {
        if (isEmpty(this.eventStatus)) {
          this.clearTreeEventStatus(nodes);
        } else if (this.treeType === treeType.LOGICAL) {
          this.setEventStatusToLogicalNodes(nodes);
        } else {
          this.setEventStatusToPhysicalNodes(nodes);
        }
      }
    },
    /**
     * 트리 노드 데이터 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} datas
     */
    setEventStatusToNodeDatas(datas) {
      if (!isEmpty(datas) && !isEmpty(this.eventStatus)) {
        if (this.treeType === treeType.LOGICAL) {
          this.setEventStatusOfLogicalNodeDatas(datas);
        } else {
          this.setEventStatusToPhysicaNodelDatas(datas);
        }
      }
    },
    /**
     * 논리 그룹 트리 노드 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} nodes
     */
    setEventStatusToLogicalNodes(nodes) {
      forEach(nodes, (node) => {
        this.setEventStatusOfLogicalNodeData(node.data);
        if (!isEmpty(node.childNodes)) {
          this.setEventStatusToLogicalNodes(node.childNodes);
        }
      });
    },
    /**
     * 논리 그룹 트리 노드 데이터 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} datas
     */
    setEventStatusOfLogicalNodeDatas(datas) {
      forEach(datas, (data) => {
        this.setEventStatusOfLogicalNodeData(data);
        if (!isEmpty(data.children)) {
          this.setEventStatusOfLogicalNodeDatas(data.children);
        }
      });
    },
    /**
     * 논리 그룹 트리 노드 타입에 맞게 데이터 객체에 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} data [description]
     */
    setEventStatusOfLogicalNodeData(data) {
      let eventlevel;
      if (data.type === treeNodeType.OBJ) {
        // OBJ
        eventlevel = this.eventStatus.OBJ[data.obj.objId];
      } else if (data.type === treeNodeType.GROUP) {
        // 논리 그룹
        eventlevel = this.eventStatus.MANAGEGROUP[data.managegroupId];
      } else if (data.type === 'root') {
        eventlevel = this.eventStatus.ROOT.LOGICAL;
      }
      if (isEmpty(eventlevel)) {
        if (!isNil(data.eventlevelId)) {
          data.eventlevelId = null;
        }
      } else {
        data.eventlevelId = eventlevel.eventlevelId;
      }
    },
    /**
     * 물리 그룹 트리 노드 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} datas
     */
    setEventStatusToPhysicalNodes(nodes) {
      forEach(nodes, (node) => {
        this.setEventStatusOfPhysicalNodeData(node.data);
        if (!isEmpty(node.childNodes)) {
          this.setEventStatusToPhysicalNodes(node.childNodes);
        }
      });
    },
    /**
     * 물리 그룹 트리 노드 데이터 객체 기준으로 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} datas
     */
    setEventStatusToPhysicaNodelDatas(datas) {
      forEach(datas, (data) => {
        this.setEventStatusOfPhysicalNodeData(data);
        if (!isEmpty(data.children)) {
          this.setEventStatusToPhysicaNodelDatas(data.children);
        }
      });
    },
    /**
     * 물리 그룹 트리 노드 타입에 맞게 데이터 객체에 이벤트 상태를 반영한다.
     * @author Ju
     * @param  {Array} data
     */
    setEventStatusOfPhysicalNodeData(data) {
      let eventlevel;
      if (data.type === treeNodeType.OBJ) {
        // OBJ
        eventlevel = this.eventStatus.OBJ[data.obj.objId];
      } else if (data.type === treeNodeType.GROUP) {
        if (!isNil(data.virtualcenterGroupId)) {
          // 가상화 센터 그룹
          eventlevel = this.eventStatus.VIRTUALCENTER_GROUP[data.virtualcenterGroupId];
        } else if (!isNil(data.hypervisorType)) {
          // 하이퍼바이저 타입
          eventlevel = this.eventStatus.HYPERVISOR_TYPE[data.hypervisorType];
        } else if (!isNil(data.objCompanyId)) {
          // 벤더
          eventlevel = this.eventStatus.OBJ_COMPANY[data.objCompanyId];
        } else if (!isNil(data.objtypeId)) {
          // 장비 구분
          eventlevel = this.eventStatus.OBJTYPE[`${data.managetypeId}.${data.objtypeId}`];
        } else if (!isNil(data.managetypeId)) {
          // 관리구분
          eventlevel = this.eventStatus.MANAGETYPE[data.managetypeId];
        }
      } else if (data.type === 'root') {
        eventlevel = this.eventStatus.ROOT.PHYISCAL;
      }
      if (isEmpty(eventlevel)) {
        if (!isNil(data.eventlevelId)) {
          data.eventlevelId = null;
        }
      } else {
        data.eventlevelId = eventlevel.eventlevelId;
      }
    },
    /**
     * 트리 이벤트 상테를 초기화한다.
     * @author Ju
     * @param  {Array} nodes
     */
    clearTreeEventStatus(nodes) {
      forEach(nodes, (node) => {
        this.clearNodeEventStatus(node);
        if (!isEmpty(node.childNodes)) {
          this.clearTreeEventStatus(node.childNodes);
        }
      });
    },
    /**
     * 트리 노드 데이터 이벤트 상태를 초기화한다.
     * @author Ju
     * @param  {Array} node
     */
    clearTreeNodeEventStatus(node) {
      if (!isNil(node.data.eventlevelId)) {
        node.data.eventlevelId = null;
      }
      if (!isEmpty(node.childNodes)) {
        forEach(node.childNodes, (child) => {
          this.clearTreeNodeEventStatus(child);
        });
      }
    },
    /**
     * 이벤트 등급 스타일 객체를 반환한다.
     * @author Ju
     * @param  {Array} data [description]
     * @return {Array}      [description]
     */
    getNodeEventLevelStyle(data) {
      if (!isNil(data.eventlevelId) && data.eventlevelId !== '') {
        const eventlevel = this.eventlevelInfo[data.eventlevelId];
        if (!isEmpty(eventlevel)) {
          return {
            content: ' ',
            display: 'inline-block',
            width: '4px',
            height: '4px',
            'border-radius': '50%',
            'background-color': eventlevel.eventlevelColorBg,
            position: 'relative',
            top: '-3px',
          };
        }
      }
      return null;
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/tree/TargetTree/mixins/event-status.js